/* Copyright (c) 2020-present CloudIO Inc */

import { ComponentSample, Item } from 'componentregistry';
import type { DivColumn } from './DivColumn';

const page: {
  root: Item<'Page'>;
  flexPanel: Item<'FlexPanel'>;
  flexItem: Item<'FlexItem'>;
  testAliasLocalDataStore: Item<'LocalDataStore'>;
  testAliasDataStoreReference: Item<'DataStoreReference'>;
  dataGrid: Item<'DataGrid'>;
  idNumberColumn: Item<'NumberColumn'>;
  divColumn: Item<'DivColumn'>;
  innerBox: Item<'Box'>;
  innerTypography: Item<'Typography'>;
  innerButton: Item<'Button'>;
} = {
  root: {
    children: ['testAliasLocalDataStore', 'flexPanel'],
    itemId: 'root',
    parent: 'root',
    props: {
      padding: { all: 0 },
      windowTitle: { type: 'text', value: 'Div Column Example' },
    },
    type: 'Page',
  },
  flexPanel: {
    children: ['flexItem'],
    itemId: 'flexPanel',
    parent: 'root',
    props: {},
    type: 'FlexPanel',
  },

  flexItem: {
    children: ['testAliasDataStoreReference'],
    itemId: 'flexItem',
    parent: 'flexPanel',
    props: {},
    type: 'FlexItem',
  },
  testAliasLocalDataStore: {
    children: [],
    itemId: 'testAliasLocalDataStore',
    parent: 'root',
    props: {
      alias: 'testAlias',
      dataFormat: 'json',
      dataSource: 'test',
      jsonData: `[
  { "id": 1, "name": "Alice" },
  { "id": 2, "name": "Bob" },
  { "id": 3, "name": "Charlie" },
  { "id": 4, "name": "Diana" },
  { "id": 5, "name": "Eve" }
]`,
    },
    type: 'LocalDataStore',
  },
  testAliasDataStoreReference: {
    children: ['dataGrid'],
    itemId: 'testAliasDataStoreReference',
    parent: 'flexItem',
    props: { alias: 'testAlias' },
    type: 'DataStoreReference',
  },
  dataGrid: {
    children: ['idNumberColumn', 'divColumn'],
    itemId: 'dataGrid',
    parent: 'testAliasDataStoreReference',
    props: {
      expandLastColumn: false,
      showBorder: true,
      updatable: { type: 'boolean', value: false },
      insertable: { type: 'boolean', value: false },
    },
    type: 'DataGrid',
  },
  idNumberColumn: {
    children: [],
    itemId: 'idNumberColumn',
    parent: 'dataGrid',
    props: {
      allowDecimal: false,
      label: { type: 'text', value: 'ID' },
      viewAttribute: 'id',
      width: 80,
    },
    type: 'NumberColumn',
  },
  divColumn: {
    children: ['innerBox'],
    itemId: 'divColumn',
    parent: 'dataGrid',
    props: {
      alignment: 'center',
      label: { type: 'text', value: 'User Info' },
      viewAttribute: '',
      width: 300,
      pinLeft: false,
      pinRight: false,
    } as Required<DivColumn>,
    type: 'DivColumn',
  },
  innerBox: {
    children: ['innerTypography', 'innerButton'],
    itemId: 'innerBox',
    parent: 'divColumn',
    props: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
    },
    type: 'Box',
  },
  innerTypography: {
    children: [],
    itemId: 'innerTypography',
    parent: 'innerBox',
    props: {
      text: { type: 'expression', value: '$.testAlias.name' },
      variant: 'body1',
    },
    type: 'Typography',
  },
  innerButton: {
    children: [],
    itemId: 'innerButton',
    parent: 'innerBox',
    props: {
      text: { type: 'text', value: 'Button' },
      startIcon: { iconName: 'plus', iconType: 'light' },
      color: 'primary',
      variant: 'contained',
      size: 'small',
    },
    type: 'Button',
  },
};

export const helpRenders: ComponentSample<'DivColumn'>[] = [
  {
    title: 'Div Column with Nested Components',
    page,
    exampleId: 'eg-01GK8QWM9KTAE6TN0ME1F8WQDIV',
    clearParentChildren: false,
    height: 420,
    visibleProps: [
      'label',
      'alignment',
      'width',
      'pinLeft',
      'pinRight',
      'visible',
      'sortable',
      'filterable',
    ],
    item: {
      children: ['innerBox'],
      itemId: 'divColumn',
      parent: 'dataGrid',
      type: 'DivColumn',
      props: {
        alignment: 'center',
        label: { type: 'text', value: 'User Info' },
        viewAttribute: '',
        width: 300,
      } as Required<DivColumn>,
    },
  },
];
